import type { PrizeTrendPoint } from '@/api/market'
import { useNotLivePage } from '@/app/notLive'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { Badge, Card, DataTable, EmptyState, formatChange, formatInteger, Icon, LoadingBlock, type Column } from '@/design'

import { useMarketQuery } from './hooks'
import { NotLiveSection } from './NotLiveSection'
import './market.css'

const DIRECTION_LABEL: Record<PrizeTrendPoint['direction'], string> = {
  rising: 'Rising',
  falling: 'Falling',
  steady: 'Steady',
}

function DirectionBadge({ point }: { point: PrizeTrendPoint }) {
  if (point.direction === 'steady') return <Badge tone="neutral">Steady</Badge>
  const tone = point.direction === 'rising' ? 'positive' : 'negative'
  return (
    <Badge tone={tone}>
      <Icon name={point.direction === 'rising' ? 'arrowUp' : 'arrowDown'} size={12} />
      {DIRECTION_LABEL[point.direction]}
    </Badge>
  )
}

const columns: Column<PrizeTrendPoint>[] = [
  { key: 'category', header: 'Category', sortValue: (row) => row.category, cell: (row) => row.category },
  { key: 'tracked', header: 'Tracked now', align: 'end', sortValue: (row) => row.trackedCount, cell: (row) => formatInteger(row.trackedCount) },
  { key: 'previous', header: 'Previous period', align: 'end', sortValue: (row) => row.previousCount, cell: (row) => formatInteger(row.previousCount) },
  {
    key: 'change',
    header: 'Change',
    align: 'end',
    sortValue: (row) => row.change ?? 0,
    cell: (row) => (row.change === null ? '—' : formatChange(row.change)),
  },
  { key: 'direction', header: 'Direction', sortValue: (row) => row.direction, cell: (row) => <DirectionBadge point={row} /> },
]

/** Which prize categories are rising or falling across tracked competitors, this period vs the last. */
export function PrizeTrendsPage({ route }: { route: RouteDef }) {
  const notLive = useNotLivePage('marketPrizeTrends')
  const trends = useMarketQuery('prize-trends', (client, filters) => client.marketPrizeTrends(filters))

  if (notLive) return <NotLiveSection route={route} />

  return (
    <SectionFrame route={route}>
      <Card title="Prize category trends" subtitle="Tracked competitions started in the selected period vs the period before" busy={trends.isFetching}>
        {trends.isPending ? <LoadingBlock rows={6} /> : null}
        {trends.isError ? <EmptyState variant="outage" compact description="Prize trends could not be loaded." /> : null}
        {trends.data ? (
          <DataTable
            caption="Prize category trends"
            columns={columns}
            rows={trends.data.data}
            rowKey={(row) => row.category}
            initialSort={{ key: 'tracked', direction: 'desc' }}
            empty={<EmptyState variant="no-data" compact title="No tracked competitions in this period" />}
          />
        ) : null}
      </Card>
    </SectionFrame>
  )
}
